import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useStore } from "./StoreContext";
import { useDocumentTitle } from "./StorefrontLayout";
import { Plus, Minus, Trash2, ShoppingBag, PawPrint } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Cart() {
  useDocumentTitle("Your cart", "Review your selections and checkout securely at Furmigo.");
  const { cart, updateQty, removeFromCart, cartTotal, itemCount } = useStore();
  const nav = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="cart-empty">
        <PawPrint className="w-16 h-16 mx-auto text-stone-300" />
        <h1 className="font-heading text-3xl font-black mt-4">Your cart is empty</h1>
        <p className="text-stone-500 mt-1">Add some treats and toys — your pet deserves it.</p>
        <Link to="/products" className="inline-flex items-center gap-1.5 mt-5 bg-stone-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-stone-800">
          <ShoppingBag className="w-4 h-4" />Start shopping
        </Link>
      </div>
    );
  }
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4" data-testid="cart-page">
      <h1 className="font-heading text-3xl font-black mb-1">Your cart</h1>
      <p className="text-stone-500 text-sm mb-5">{itemCount} item{itemCount === 1 ? "" : "s"}</p>

      <div className="bg-white rounded-2xl border border-stone-200 divide-y divide-stone-200">
        {cart.map(line => (
          <div key={line.product_id} className="p-3 sm:p-4 flex items-center gap-3" data-testid={`cart-line-${line.product_id}`}>
            <div className="w-16 h-16 rounded-xl bg-stone-100 overflow-hidden shrink-0">
              {line.image_path ? <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files-public/${line.image_path}`} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm leading-tight line-clamp-2">{line.name}</div>
              <div className="text-xs text-stone-500 mt-0.5">{inr(line.sale_price)} each</div>
            </div>
            <div className="flex items-center border border-stone-300 rounded-full">
              <button onClick={() => updateQty(line.product_id, line.qty - 1)} className="p-1.5 hover:bg-stone-100 rounded-full" data-testid={`cart-minus-${line.product_id}`}><Minus className="w-3 h-3" /></button>
              <span className="px-2 text-sm font-bold w-7 text-center">{line.qty}</span>
              <button onClick={() => updateQty(line.product_id, line.qty + 1)} className="p-1.5 hover:bg-stone-100 rounded-full" data-testid={`cart-plus-${line.product_id}`}><Plus className="w-3 h-3" /></button>
            </div>
            <div className="text-right w-20">
              <div className="font-heading font-bold">{inr(line.qty * line.sale_price)}</div>
              <button onClick={() => removeFromCart(line.product_id)} className="text-rose-600 hover:underline text-xs flex items-center gap-1 mt-0.5 ml-auto" data-testid={`cart-remove-${line.product_id}`}>
                <Trash2 className="w-3 h-3" />Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-stone-200 p-5 mt-4 space-y-2">
        <Row label="Subtotal" v={cartTotal} />
        <Row label="Delivery" v={0} hint="Calculated at checkout" />
        <div className="border-t border-stone-200 pt-2">
          <Row label="Estimated total" v={cartTotal} bold />
        </div>
        <button onClick={() => nav("/checkout")} className="mt-3 w-full bg-stone-900 text-white py-3.5 rounded-full font-bold hover:bg-stone-800" data-testid="cart-checkout-btn">
          Proceed to checkout
        </button>
        <Link to="/products" className="block text-center mt-1 text-sm font-semibold text-amber-700 hover:underline">Continue shopping</Link>
      </div>
    </div>
  );
}
const Row = ({ label, v, bold, hint }) => (
  <div className="flex items-center justify-between">
    <div>
      <div className={bold ? "font-bold text-lg" : "text-stone-700 text-sm"}>{label}</div>
      {hint && <div className="text-xs text-stone-400">{hint}</div>}
    </div>
    <div className={`font-mono ${bold ? "font-bold text-lg" : ""}`}>{inr(v)}</div>
  </div>
);
